import React from "react";
import styled from "styled-components";
import { RoundButcon } from "../__Internal__";
import { useEvent } from "../hooks/useEvent";
import { Icon } from "../Icon";
import { style } from "../style";

/**
 * IMPORTANT: This container element _must_ use `display: grid` so it does not
 * attempt to stretch the inner container. If it uses `display: flex`, the inner
 * container will stretch to fill the available _max-width_ of the outer elements.
 */
const OuterTabsContainer = styled.div`
  display: grid;
  position: relative;
`;

const ScrollableTabsContainer = styled.div`
  position: relative;
  overflow-x: scroll;
  scrollbar-width: none;
  scroll-behavior: smooth;
`;

const InnerTabsContainer = styled.div`
  display: flex;
  position: relative;
  align-items: end;
  width: max-content;
`;

const ScrollButtonContainer = styled.div<{ $position: "left" | "right"; $visible: boolean }>`
  all: unset;
  align-items: center;
  justify-content: center;
  z-index: 2;

  display: ${(props) => (props.$visible ? "flex" : "none")};
  background: ${style.colors.white};

  height: 100%;
  position: absolute;
  top: 0;
  ${(props) => {
    if (props.$position === "left") {
      return {
        left: 0,
        boxShadow: `${style.colors.white} 16px 0px 16px 8px;`,
      };
    } else {
      return {
        right: 0,
        boxShadow: `${style.colors.white} -16px 0px 16px 8px;`,
      };
    }
  }}
`;

type ScrollButtonState = {
  showLeft: boolean;
  showRight: boolean;
};

type HeadlessChildrenProps<Tab extends { id: string }> = {
  tab: Tab;
  selected: boolean;
  onSelect(id: string): void;
  ref?: React.Ref<HTMLButtonElement>;
};

type HeadlessTabsProps<Tab extends { id: string }> = {
  options: Tab[];
  selected: string | null;
  onSelect: (id: string) => void;
  "data-testid"?: string;
  children(props: HeadlessChildrenProps<Tab>): React.ReactElement;
};

export const HeadlessTabs = <Tab extends { id: string }>(props: HeadlessTabsProps<Tab>) => {
  const { selected, options, onSelect } = props;
  const testId = props["data-testid"] ?? "tabs";

  /**
   * Create a ref for the scrollable container so that we can adjust the scrolled position as needed.
   */
  const scrollableTabContainerRef = React.useRef<HTMLDivElement>(null);

  /**
   * Create a ref for the selected tab so that we can focus it when it changes.
   */
  const selectedTabRef = React.useRef<HTMLButtonElement>(null);

  const scrollSelectedIntoView = useEvent(() => {
    const selectedTab = selectedTabRef.current;
    const container = scrollableTabContainerRef.current;

    if (!selectedTab || !container) {
      return;
    }

    /**
     * Scroll the selected tab into view if it's not entirely visible.
     */
    const selectedLeft = selectedTab.offsetLeft;
    const selectedRight = selectedLeft + selectedTab.clientWidth;
    const containerLeft = Math.ceil(container.scrollLeft);
    const containerRight = containerLeft + container.clientWidth;

    /**
     * This is additional padding to account for the scroll button width and box shadow.
     */
    const scrollPadding = 60;

    if (selectedLeft < containerLeft) {
      container.scrollLeft = selectedLeft - scrollPadding;
    } else if (selectedRight > containerRight) {
      container.scrollLeft = selectedRight - container.clientWidth + scrollPadding;
    }
  });

  /**
   * Whenever the selected tab changes, scroll it into view.
   */
  React.useLayoutEffect(() => {
    queueMicrotask(() => scrollSelectedIntoView());
  }, [selected]);

  const [scrollButtonState, recalculateScrollState] = React.useReducer(
    (state: ScrollButtonState) => {
      const container = scrollableTabContainerRef.current;

      if (!container) {
        return state;
      }

      const showLeft = container.scrollLeft > 0;
      const showRight = Math.ceil(container.scrollLeft) + container.clientWidth < container.scrollWidth;

      if (showLeft === state.showLeft && showRight === state.showRight) {
        return state;
      }

      return {
        showLeft,
        showRight,
      };
    },
    { showLeft: false, showRight: false }
  );

  /**
   * If the order and/or length of the options changes, recalculate the scroll state.
   * This will also trigger when the component is mounted.
   */
  React.useLayoutEffect(recalculateScrollState, [options.map((tab) => tab.id).join(",")]);

  /**
   * Track whether a scroll event happens so that we can calculate whether to show the scroll buttons.
   */
  React.useEffect(() => {
    const container = scrollableTabContainerRef.current;

    if (!container) {
      return;
    }

    container.addEventListener("scroll", recalculateScrollState);
    window.addEventListener("resize", recalculateScrollState);

    return () => {
      container.removeEventListener("scroll", recalculateScrollState);
      window.removeEventListener("resize", recalculateScrollState);
    };
  }, [scrollableTabContainerRef.current]);

  const onButtonScrollLeft = useEvent<React.MouseEventHandler>((ev) => {
    ev.preventDefault();

    const container = scrollableTabContainerRef.current;

    if (!container) {
      return;
    }

    container.scrollLeft -= container.clientWidth;
  });

  const onButtonScrollRight = useEvent<React.MouseEventHandler>((ev) => {
    ev.preventDefault();

    const container = scrollableTabContainerRef.current;

    if (!container) {
      return;
    }

    container.scrollLeft += container.clientWidth;
  });

  const onKeyDown = useEvent<React.KeyboardEventHandler<HTMLDivElement>>((ev) => {
    let changeIndex = 0;

    if (ev.key === "ArrowRight") {
      changeIndex = 1;
    } else if (ev.key === "ArrowLeft") {
      changeIndex = -1;
    }

    if (changeIndex === 0) {
      return;
    }

    ev.preventDefault();

    const currentId = selected ?? options[0].id;
    const currentIndex = options.findIndex((tab) => tab.id === currentId);

    const nextIndex = (options.length + currentIndex + changeIndex) % options.length;
    const nextTabId = options[nextIndex].id;

    onSelect(nextTabId);

    /**
     * After changing the selected tag with keyboard controls,
     * wait until the next render is complete and then focus the new tag.
     */
    queueMicrotask(() => selectedTabRef?.current?.focus());
  });

  return (
    <OuterTabsContainer data-testid={testId}>
      <ScrollableTabsContainer onKeyDown={onKeyDown} ref={scrollableTabContainerRef}>
        <InnerTabsContainer role="tablist" aria-orientation="horizontal">
          {options.map((tab) =>
            props.children({
              tab,
              selected: tab.id === selected,
              onSelect,
              ref: tab.id === selected ? selectedTabRef : undefined,
            })
          )}
        </InnerTabsContainer>
      </ScrollableTabsContainer>

      <ScrollButtonContainer $position="left" $visible={scrollButtonState.showLeft}>
        <RoundButcon type="button" onClick={onButtonScrollLeft}>
          <Icon icon="arrow-left-long" />
        </RoundButcon>
      </ScrollButtonContainer>

      <ScrollButtonContainer $position="right" $visible={scrollButtonState.showRight}>
        <RoundButcon type="button" onClick={onButtonScrollRight}>
          <Icon icon="arrow-right-long" />
        </RoundButcon>
      </ScrollButtonContainer>
    </OuterTabsContainer>
  );
};
